/** @jsx jsx */
import { jsx, Flex } from 'theme-ui';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from './Button';
import Link from './Link';
import Spinner from './Spinner';
import { TicketIcon, TicketSeriesIcon } from './Icon';
import { loadRoutePrices } from '../state/routePrices';
import { formatPrice } from '../utils/formatters';
import useRouteCardTracking from '../hooks/useRouteCardTracking';

// link looks like https://liput.matkahuolto.fi/connectionlist?departurePlaceId=p1001&arrivalPlaceId=p1317
// The route id used to look up the price is `${departurePlaceId}-${arrivalPlaceId}`, e.g. p1001-p1317.
const routeIdFromLink = (link) => {
  if (!link) return null;
  try {
    const { searchParams } = new URL(link);
    const dep = searchParams.get('departurePlaceId');
    const arr = searchParams.get('arrivalPlaceId');
    return dep && arr ? `${dep}-${arr}` : null;
  } catch {
    return null;
  }
};

export default ({ title, routes = [] }) => {
  const dispatch = useDispatch();
  const prices = useSelector((state) => state.routePrices.prices);

  const cards = useMemo(() => routes.filter((card) => !!card), [routes]);

  useEffect(() => {
    dispatch(loadRoutePrices());
  }, [dispatch]);

  if (!cards.length) {
    return null;
  }

  if (prices === null) {
    return (
      <div sx={{ position: 'relative', height: '100px', maxWidth: 700 }}>
        <Spinner />
      </div>
    );
  }

  const cardsWithPrice = cards
    .map((card) => ({ card, price: prices[routeIdFromLink(card.link)] }))
    // Hide routes the API has no fare for, like ProductList drops products with no price.
    .filter(({ price }) => typeof price?.price === 'number');

  if (!cardsWithPrice.length) {
    return null;
  }

  return (
    <Flex sx={{ flexDirection: 'column', width: '100%', gap: 3, mb: 4 }}>
      {title && <h2 sx={{ m: 0 }}>{title}</h2>}
      {cardsWithPrice.map(({ card, price }, i) => (
        <TicketRouteCard
          {...card}
          name={price.name}
          price={price.price}
          position={i + 1}
          key={card?.contentful_id || i}
        />
      ))}
    </Flex>
  );
};

const TicketRouteCard = ({
  ribbon,
  ribbonColor,
  icon,
  leftContent,
  rightContent,
  rightContentSubtitle,
  ctaPriceButtonPrefix,
  link,
  name,
  price,
  position,
}) => {
  const formattedPrice = formatPrice(price);
  const ctaButton = ctaPriceButtonPrefix ? `${ctaPriceButtonPrefix} ${formattedPrice}` : formattedPrice;
  const { ref, onClick } = useRouteCardTracking(name, position, link);

  return (
    <Flex
      ref={ref}
      sx={{
        borderRadius: 2,
        border: '2px solid',
        borderColor: 'border',
        flexDirection: 'column',
        width: '100%',
        maxWidth: [null, null, null, 750],
        position: 'relative',
      }}
    >
      {ribbon && (
        <Flex
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          <Label color={ribbonColor} text={ribbon} />
        </Flex>
      )}

      <Flex
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
          pl: [2, null, 5],
          py: ribbon ? ['36px', null, '40px'] : [4, null, '23px'],
          px: 3,
        }}
      >
        <Flex
          sx={{
            flex: [1, null, 1.8],
            minWidth: 0,
            alignItems: 'center',
            order: [1, null, 0],
            gap: [3, 4, 0],
          }}
        >
          <Flex
            sx={{
              flex: ['none', null, 1],
              justifyContent: ['flex-start', null, 'center'],
              alignItems: 'center',
              order: [0, null, 1],
              ml: [2, null, 0],
              minWidth: 40,
            }}
          >
            {icon === 'round-trip' ? (
              <TicketSeriesIcon width="40" height="40" aria-hidden="true" />
            ) : (
              <TicketIcon width="40" height="40" aria-hidden="true" />
            )}
          </Flex>

          <Flex
            sx={{
              flex: [1, null, 0.8],
              minWidth: 0,
              flexDirection: 'column',
              order: [1, null, 0],
              alignItems: 'flex-start',
            }}
          >
            {leftContent && (
              <span
                sx={{
                  width: '100%',
                  fontSize: 18,
                  fontWeight: 'medium',
                  hyphens: 'auto',
                  overflowWrap: 'anywhere',
                  textAlign: 'left',
                }}
              >
                {leftContent}
              </span>
            )}
            {name && (
              <span sx={{ width: '100%', fontSize: 1, color: 'black', overflowWrap: 'anywhere', textAlign: 'left' }}>
                {name}
              </span>
            )}
          </Flex>
        </Flex>

        {rightContent && (
          <Flex
            sx={{
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              display: ['none', null, 'flex'],
            }}
          >
            {link ? (
              <Link
                to={link}
                noTitleFix
                onClick={onClick}
                sx={{ textDecoration: 'none', color: 'primary', ':hover': { textDecoration: 'underline' } }}
              >
                <span sx={{ textAlign: 'left', fontSize: 18, fontWeight: 'medium' }}>{rightContent}</span>
              </Link>
            ) : (
              <span sx={{ textAlign: 'left', fontSize: 18, fontWeight: 'medium' }}>{rightContent}</span>
            )}
            {rightContentSubtitle && (
              <span sx={{ fontSize: 1, color: 'black', textAlign: 'left' }}>{rightContentSubtitle}</span>
            )}
          </Flex>
        )}

        {link && (
          <Flex sx={{ flex: ['0 0 auto', null, 1], flexShrink: 0, justifyContent: 'flex-end', order: [3, null, 0] }}>
            <Button
              as={Link}
              variant="small"
              to={link}
              noTitleFix
              onClick={onClick}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', textAlign: 'center' }}
            >
              {ctaButton}
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const Label = ({ color, text }) => (
  <Flex
    sx={{
      bg: color || 'primary',
      color: 'white',
      borderRadius: 1,
      fontSize: '11px',
      mx: 1,
      mt: 1,
      py: 1,
      px: 2,
    }}
  >
    <span>{text}</span>
  </Flex>
);
